import {
  Switch as MuiSwitch,
  SwitchProps as MuiSwitchProps,
  useThemeProps,
} from '@mui/material';
import * as React from 'react';

export interface SwitchProps
  extends Omit<
      MuiSwitchProps,
      | keyof UnsupportedProps
      | 'checkedIcon'
      | 'disableFocusRipple'
      | 'disapbleRipple'
      | 'icon'
      | 'LinkComponent'
      | 'readOnly'
    >,
    UnsupportedProps {
  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  disableRipple?: MuiSwitchProps['disableRipple'];

  /**
   * Callback fired when the state is changed.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event The event source of the callback.
   * You can pull out the new checked state by accessing `event.target.checked` (boolean).
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Deprecate props that aren't supported in Unify.
 */
type UnsupportedProps = {
  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  color?: MuiSwitchProps['color'];
};

// This wrapper is necessary to change the typing of the `onChange` prop from
// `(event: ChangeEvent, checked:bool) => void` to `(event: ChangeEvent) => void`
// in order to avoid unnecessary typing conflicts with `UiProps.onChange`.
/**
 * Switches allow customers to choose between two mutually exclusive choices.
 *
 * Demos:
 *
 * - [Switch](https://unify.workiva.org/components/switch?tab=code)
 *
 * API:
 *
 * - [Switch API](https://unify.workiva.org/components/switch?tab=props)
 */
export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  function Switch(inProps, ref) {
    const props = useThemeProps({ props: inProps, name: 'MuiSwitch' });
    const { onChange, ...rest } = props;
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      return onChange?.(event);
    };

    return <MuiSwitch {...rest} onChange={handleChange} ref={ref} />;
  }
);
export default Switch;
